/**
 * Client-only comment composer state.
 */
import { create } from 'zustand';

import type { SocialCommentVo } from 'models';

interface CommentComposerActions {
    setShowCreateComment: (show: boolean) => void;
    setPostToCommentOn: (postId: string) => void;
    setOpenReplyForComment: (commentId: string | null) => void;
    /** Record a just-created comment. */
    commentCreated: (comment: SocialCommentVo) => void;
    /** Clear session-created comments. */
    recentCommentsCleared: () => void;
    /**
     * Session-end teardown.
     *
     * This is a module-level singleton, so without it a comment one account just posted (and its
     * open reply target) survives a logout and is rendered to whoever signs in next in the same
     * tab, before their own comments query settles.
     */
    endSession: () => void;
}

interface CommentComposerState {
    showCreateComment: boolean;
    /** Post id the create-comment modal targets ('' = none). */
    postToCommentOn: string;
    /** Comment id whose reply thread is open (null = none). */
    openReplyForCommentId: string | null;
    recentlyCreatedComments: SocialCommentVo[];
    actions: CommentComposerActions;
}

export const useCommentComposerStore = create<CommentComposerState>()((set) => ({
    showCreateComment: false,
    postToCommentOn: '',
    openReplyForCommentId: null,
    recentlyCreatedComments: [],
    actions: {
        setShowCreateComment: (show) => set({ showCreateComment: show }),
        setPostToCommentOn: (postId) => set({ postToCommentOn: postId }),
        setOpenReplyForComment: (commentId) => set({ openReplyForCommentId: commentId }),
        commentCreated: (comment) =>
            set((state) => ({ recentlyCreatedComments: [...state.recentlyCreatedComments, comment] })),
        recentCommentsCleared: () =>
            set((state) => (state.recentlyCreatedComments.length === 0 ? state : { recentlyCreatedComments: [] })),
        endSession: () =>
            set({
                showCreateComment: false,
                postToCommentOn: '',
                openReplyForCommentId: null,
                recentlyCreatedComments: [],
            }),
    },
}));
